/**
 * Human-input contract helpers
 *
 * Mirrors the browser-relevant llm-runtime 0.7 ask_user_input rules without
 * bundling the server runtime package into the frontend.
 *
 * Recent changes:
 * - Normalizes browser free-text answers before either HITL entry point can submit them.
 * - Canonicalizes omitted single-question composer capability while preserving an explicit allowOther false.
 * - Validates persisted answers strictly so historical omitted capability is never inferred after completion.
 * - Treats the CRM composer as an implicit free-form answer surface for a
 *   single single-select question, independent of inline Other rendering.
 * - Added canonical answered/cancelled outcome validation and allowOther gating.
 */

import {
  ChatMessage,
  HUMAN_INPUT_FREEFORM_MAX_LENGTH,
  HumanInputAnswer,
  HumanInputAnsweredOutcome,
  HumanInputQuestion,
  HumanInputRequest
} from '../models/ChatMessage';

export type HumanInputValidationResult =
  | { ok: true; outcome: HumanInputAnswer }
  | { ok: false; error: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(value: Record<string, unknown>, allowedKeys: string[]): boolean {
  const allowed = new Set(allowedKeys);
  return Object.keys(value).every((key) => allowed.has(key));
}

function invalid(error: string): HumanInputValidationResult {
  return { ok: false, error };
}

export function normalizeHumanInputFreeform(value: string): string | null {
  if (value.length > HUMAN_INPUT_FREEFORM_MAX_LENGTH) {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

export function parseHumanInputRequest(rawArguments: string): HumanInputRequest | null {
  try {
    const parsed = JSON.parse(rawArguments);
    return isRecord(parsed) && Array.isArray(parsed.questions)
      ? parsed as unknown as HumanInputRequest
      : null;
  } catch {
    return null;
  }
}

export function getComposerHumanInputQuestion(request: HumanInputRequest | null | undefined): HumanInputQuestion | null {
  const question = Array.isArray(request?.questions) ? request.questions[0] : null;
  if (
    !request ||
    (request.type ?? 'single-select') !== 'single-select' ||
    !Array.isArray(request.questions) ||
    request.questions.length !== 1 ||
    !isRecord(question) ||
    (question.allowOther !== undefined && question.allowOther !== true)
  ) {
    return null;
  }

  return question as unknown as HumanInputQuestion;
}

export function canonicalizeComposerHumanInputToolCalls(
  toolCalls: ChatMessage['tool_calls']
): ChatMessage['tool_calls'] {
  if (!toolCalls?.length) {
    return toolCalls;
  }

  return toolCalls.map((toolCall) => {
    if (toolCall.function.name !== 'ask_user_input') {
      return toolCall;
    }

    const request = parseHumanInputRequest(toolCall.function.arguments);
    const question = getComposerHumanInputQuestion(request);
    if (!request || !question || !isRecord(question) || question.allowOther !== undefined) {
      return toolCall;
    }

    return {
      ...toolCall,
      function: {
        ...toolCall.function,
        arguments: JSON.stringify({
          ...request,
          questions: [{
            ...question,
            allowOther: true
          }]
        })
      }
    };
  });
}

export function normalizeHumanInputOutcome(
  request: HumanInputRequest,
  rawOutcome: unknown,
  options: { allowImplicitComposer?: boolean } = {}
): HumanInputValidationResult {
  const allowImplicitComposer = options.allowImplicitComposer ?? true;
  const selectionType = request.type ?? 'single-select';
  if (selectionType !== 'single-select' && selectionType !== 'multiple-select') {
    return invalid('Human input request type must be single-select or multiple-select');
  }

  if (!Array.isArray(request.questions) || request.questions.length === 0) {
    return invalid('Human input request must contain at least one question');
  }

  const questions = new Map<string, { optionIds: Set<string>; allowOther: boolean }>();
  for (const question of request.questions) {
    if (!isRecord(question) || typeof question.id !== 'string' || !question.id.trim()) {
      return invalid('Every human input question must have a non-empty id');
    }
    if (questions.has(question.id)) {
      return invalid(`Duplicate human input question id: ${question.id}`);
    }
    if (!Array.isArray(question.options) || question.options.length < 2) {
      return invalid(`Question "${question.id}" must contain at least two options`);
    }

    const optionIds = new Set<string>();
    for (const option of question.options) {
      if (!isRecord(option) || typeof option.id !== 'string' || !option.id.trim()) {
        return invalid(`Question "${question.id}" contains an option without a non-empty id`);
      }
      if (optionIds.has(option.id)) {
        return invalid(`Question "${question.id}" contains duplicate option id: ${option.id}`);
      }
      optionIds.add(option.id);
    }

    const allowOther =
      question.allowOther === true ||
      (
        allowImplicitComposer &&
        question.allowOther === undefined &&
        selectionType === 'single-select' &&
        request.questions.length === 1
      );
    if (selectionType === 'multiple-select' && allowOther) {
      return invalid(`Question "${question.id}" cannot enable Other for multiple-select input`);
    }
    questions.set(question.id, { optionIds, allowOther });
  }

  if (!isRecord(rawOutcome) || typeof rawOutcome.status !== 'string') {
    return invalid('Human input response must be an answered or cancelled outcome');
  }

  if (rawOutcome.status === 'cancelled') {
    if (!hasOnlyKeys(rawOutcome, ['status', 'reason', 'message'])) {
      return invalid('Cancelled human input contains unknown fields');
    }
    if (
      rawOutcome.reason !== 'rejected' &&
      rawOutcome.reason !== 'skipped' &&
      rawOutcome.reason !== 'dismissed' &&
      rawOutcome.reason !== 'timeout'
    ) {
      return invalid('Cancelled human input has an invalid reason');
    }
    if (rawOutcome.message !== undefined && typeof rawOutcome.message !== 'string') {
      return invalid('Cancelled human input message must be text');
    }

    return {
      ok: true,
      outcome: {
        status: 'cancelled',
        reason: rawOutcome.reason,
        ...(rawOutcome.message ? { message: rawOutcome.message } : {})
      }
    };
  }

  if (
    rawOutcome.status !== 'answered' ||
    !hasOnlyKeys(rawOutcome, ['status', 'answers']) ||
    !isRecord(rawOutcome.answers)
  ) {
    return invalid('Answered human input must contain only status and answers');
  }

  const answerIds = Object.keys(rawOutcome.answers);
  if (answerIds.length !== questions.size || answerIds.some((id) => !questions.has(id))) {
    return invalid('Answered human input must answer every declared question exactly once');
  }

  const normalizedAnswers = Object.create(null) as Record<string, string | string[]>;
  for (const [questionId, question] of questions) {
    const answer = rawOutcome.answers[questionId];
    if (selectionType === 'single-select') {
      if (typeof answer !== 'string' || !answer.trim()) {
        return invalid(`Question "${questionId}" requires one non-empty answer`);
      }
      if (question.optionIds.has(answer)) {
        normalizedAnswers[questionId] = answer;
        continue;
      }
      if (!question.allowOther) {
        return invalid(`Question "${questionId}" does not allow free-form answers`);
      }
      const normalizedAnswer = normalizeHumanInputFreeform(answer);
      if (!normalizedAnswer) {
        return invalid(
          `Free-text input must be ${HUMAN_INPUT_FREEFORM_MAX_LENGTH} characters or fewer`
        );
      }
      normalizedAnswers[questionId] = normalizedAnswer;
      continue;
    }

    if (!Array.isArray(answer) || answer.length === 0) {
      return invalid(`Question "${questionId}" requires at least one selected option`);
    }
    const selectedIds = new Set<string>();
    for (const selectedId of answer) {
      if (typeof selectedId !== 'string' || !question.optionIds.has(selectedId)) {
        return invalid(`Question "${questionId}" contains an invalid selected option`);
      }
      if (selectedIds.has(selectedId)) {
        return invalid(`Question "${questionId}" contains duplicate selected options`);
      }
      selectedIds.add(selectedId);
    }
    normalizedAnswers[questionId] = [...selectedIds];
  }

  const outcome: HumanInputAnsweredOutcome = {
    status: 'answered',
    answers: normalizedAnswers
  };
  return { ok: true, outcome };
}

export function parseCanonicalHumanInputAnswer(
  request: HumanInputRequest,
  content: string
): HumanInputAnsweredOutcome | null {
  try {
    const result = normalizeHumanInputOutcome(
      request,
      JSON.parse(content),
      { allowImplicitComposer: false }
    );
    return result.ok && result.outcome.status === 'answered' ? result.outcome : null;
  } catch {
    return null;
  }
}
